import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { palette } from "../../../assets/styles/Palette";
import {
  H1,
  H3,
  Body1,
  Body2,
  Caption1,
} from "../../../assets/styles/Typography";
import { Button } from "../../../assets/styles/ButtonStyle";
import MoleculeHeader from "../../Global/components/molecules/MoleculeHeader";
import { CustomTextarea } from "../../../assets/styles/InputStyle";
import OrganismDisplayViewer from "../components/organisms/OrganismDisplayViewer";
import {
  AUTODISPLAY_SELECTED_CONTENT,
  AUTODISPLAY_ACTIVE_HTML,
  AUTODISPLAY_ACTIVE_DPI_INFO,
} from "../../AtomStates";
import { useAtom } from "jotai";
import { viewDetailActivateRequest } from "../../../utils/indexedDB";
import PopupWrap from "../../../assets/styles/Popup";

const PageAutodisplayView = () => {
  const navigate = useNavigate();
  const [autoDisplaySelectedContent, setAutoDisplaySelectedContent] = useAtom(
    AUTODISPLAY_SELECTED_CONTENT
  );
  const [autoDisplayActiveHTML, setAutoDisplayActiveHTML] = useAtom(
    AUTODISPLAY_ACTIVE_HTML
  );
  const [autoDisplayActiveDPIInfo, setAutoDisplayActiveDPIInfo] = useAtom(
    AUTODISPLAY_ACTIVE_DPI_INFO
  );
  const { path } = useParams(); // URL에서 path 파라미터 가져오기

  // 폼 상태
  const [formData, setFormData] = useState({
    path: "",
    activecode: "",
    location: "",
  });

  // IP 주소 상태
  const [userIP, setUserIP] = useState("");
  const [ipLoading, setIpLoading] = useState(true);
  const [ipError, setIpError] = useState("");

  // 비밀번호 표시 상태
  const [showPassword, setShowPassword] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [activateTimestamp, setActivateTimestamp] = useState("");

  // 비밀번호 표시/숨기기 토글
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // 복사/붙여넣기 방지 함수들
  const handlePreventCopy = (e) => {
    e.preventDefault();
    return false;
  };

  const handlePreventPaste = (e) => {
    e.preventDefault();
    return false;
  };

  const handlePreventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // IP 주소 가져오기
  const fetchUserIP = async () => {
    try {
      setIpLoading(true);
      setIpError("");

      // ipapi.co API 사용 (무료, CORS 지원)
      const response = await fetch("https://ipapi.co/json/");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.ip) {
        setUserIP(data.ip);
      } else {
        throw new Error("IP 주소를 가져올 수 없습니다");
      }
    } catch (error) {
      console.error("IP 주소 가져오기 실패:", error);
      setIpError(error.message || "IP 주소를 가져오는데 실패했습니다");

      // 대체 API 시도 (fallback)
      try {
        const fallbackResponse = await fetch(
          "https://api.ipify.org?format=json"
        );
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.ip) {
          setUserIP(fallbackData.ip);
          setIpError("");
        }
      } catch (fallbackError) {
        console.error("대체 IP API도 실패:", fallbackError);
      }
    } finally {
      setIpLoading(false);
    }
  };

  // 컴포넌트 마운트 시 IP 주소 가져오기
  useEffect(() => {
    fetchUserIP();
  }, []);

  // 폼 유효성 검사
  const isFormValid = () => {
    return (
      formData.path.trim() !== "" &&
      formData.activecode.trim() !== "" &&
      formData.location.trim() !== ""
    );
  };

  // 폼 변경 핸들러
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 연결 버튼 클릭 핸들러
  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setShowErrorPopup(false);

      const activateTimestamp = Date.now();
      setActivateTimestamp(activateTimestamp);

      const data = {
        viewId: formData.path,
        activateCode: formData.activecode,
        activateLocation: formData.location,
        activateIp: userIP,
        activateTimestamp: activateTimestamp,
      };

      const response = await viewDetailActivateRequest(data);

      // console.log("response", response);

      setAutoDisplayActiveHTML(response.htmlCode);
      setAutoDisplayActiveDPIInfo(response.deviceDPIInfo);
      // navigate(`/AutodisplayView`);
      navigate(`/AutodisplayView/${formData.path}`);
    } catch (error) {
      console.error("연결 중 오류 발생:", error);

      // 서버 응답 메시지 추출
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "연결 중 오류가 발생했습니다. 다시 시도해주세요.";

      setErrorMessage(serverMessage);
      setShowErrorPopup(true);
    } finally {
      setIsConnecting(false);
    }
  };

  // 연결 해제 핸들러
  const handleDisconnect = () => {
    // 메인 페이지로 돌아가기
    navigate("/AutodisplayView");
  };

  // 초기화 핸들러
  const handleReset = () => {
    setFormData({ path: "", activecode: "", location: "" });
  };

  // URL에 path가 있으면 디스플레이 뷰어 표시
  if (path) {
    return (
      <AutodisplayContainer>
        <OrganismDisplayViewer
          path={path}
          activateCode={formData.activecode}
          activateTimestamp={activateTimestamp}
        />
      </AutodisplayContainer>
    );
  }

  // path가 없으면 폼 UI 표시
  return (
    <AutodisplayContainer>
      <MoleculeHeader />

      <MainContent>
        <HeaderSection>
          <H1 color="gray800">AutoDisplay 연결</H1>
          <Body1 color="gray600" style={{ marginTop: "12px" }}>
            디스플레이와 연결하기 위해 필요한 정보를 입력해주세요.
          </Body1>
        </HeaderSection>

        <FormSection>
          {/* IP 주소 정보 섹션 */}
          {/* <IPInfoSection>
            <IPInfoLabel>
              <Body1 color="gray700">현재 IP 주소</Body1>
            </IPInfoLabel>
            <IPInfoValue>
              {ipLoading ? (
                <Caption1 color="gray500">IP 주소를 확인 중...</Caption1>
              ) : ipError ? (
                <Caption1 color="error">{ipError}</Caption1>
              ) : (
                <Body2 color="primary">{userIP}</Body2>
              )}
            </IPInfoValue>
            {!ipLoading && ipError && (
              <RetryButton onClick={fetchUserIP}>
                <Caption1 color="primary">다시 시도</Caption1>
              </RetryButton>
            )}
          </IPInfoSection> */}

          <FormContainer>
            <FormField>
              <FormLabel>
                <Body1 color="gray700">디스플레이 설치 장소</Body1>
                <RequiredMark>*</RequiredMark>
              </FormLabel>
              <CustomTextarea
                width="100%"
                rows={1}
                placeholder="디스플레이 설치 장소를 입력해주세요."
                value={formData.location}
                onChange={(e) => handleFormChange("location", e.target.value)}
                status={formData.location ? "valid" : "default"}
              />
              <FormLabel>
                <Body1 color="gray700">경로 ID</Body1>
                <RequiredMark>*</RequiredMark>
              </FormLabel>
              <CustomTextarea
                width="100%"
                rows={1}
                placeholder="콘텐츠 경로 ID를 입력해주세요."
                value={formData.path}
                onChange={(e) => handleFormChange("path", e.target.value)}
                status={formData.path ? "valid" : "default"}
              />
              <FormLabel>
                <Body1 color="gray700">활성화 CODE</Body1>
                <RequiredMark>*</RequiredMark>
              </FormLabel>
              <PasswordInputContainer>
                <PasswordInput
                  type={showPassword ? "text" : "password"}
                  placeholder="활성화 CODE를 입력해주세요"
                  value={formData.activecode}
                  onChange={(e) =>
                    handleFormChange("activecode", e.target.value)
                  }
                  // onCopy={handlePreventCopy}
                  // onPaste={handlePreventPaste}
                  onContextMenu={handlePreventContextMenu}
                  onDragStart={handlePreventCopy}
                  onDrop={handlePreventPaste}
                  autoComplete="off"
                  spellCheck="false"
                />
                <PasswordToggleButton
                  type="button"
                  onClick={togglePasswordVisibility}
                  title={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                >
                  <i
                    className={showPassword ? "fa fa-eye-slash" : "fa fa-eye"}
                  ></i>
                </PasswordToggleButton>
              </PasswordInputContainer>
              {/* <HelperText>
                <Caption1 color="gray500">
                  디스플레이 기기에서 생성된 활성화 코드를 입력하세요
                </Caption1>
              </HelperText> */}
            </FormField>

            <ButtonSection>
              <Button
                Large
                PrimaryLightest
                Fill
                onClick={handleReset}
                style={{ minWidth: "120px" }}
              >
                <Body1 color="primary">초기화</Body1>
              </Button>

              <Button
                Large
                Primary
                Fill
                disabled={!isFormValid() || isConnecting}
                onClick={handleConnect}
                style={{ minWidth: "120px" }}
              >
                <Body1 color="white">
                  {isConnecting ? "연결 중..." : "연결"}
                </Body1>
              </Button>
            </ButtonSection>
          </FormContainer>

          {/* <InfoSection>
            <InfoBox>
              <H3 color="gray800" style={{ marginBottom: "16px" }}>연결 안내</H3>
              <InfoList>
                <InfoItem>
                  <Body2 color="gray700">1. 디스플레이 기기에서 활성화 코드를 확인하세요</Body2>
                </InfoItem>
                <InfoItem>
                  <Body2 color="gray700">2. 표시할 콘텐츠의 경로를 정확히 입력하세요</Body2>
                </InfoItem>
                <InfoItem>
                  <Body2 color="gray700">3. 연결 후 디스플레이에서 콘텐츠 확인이 가능합니다</Body2>
                </InfoItem>
              </InfoList>
            </InfoBox>
          </InfoSection> */}
        </FormSection>
      </MainContent>

      {/* 에러 팝업 */}
      {showErrorPopup && (
        <PopupWrap
          Warning
          title="연결 실패"
          message={errorMessage}
          buttonType="Outline"
          confirmText="확인"
          isModal={false}
          onConfirm={() => setShowErrorPopup(false)}
        />
      )}
    </AutodisplayContainer>
  );
};

export default PageAutodisplayView;

// 스타일 컴포넌트들
const AutodisplayContainer = styled.div`
  width: 100%;
  max-width: 2330px;
  margin: 0 auto;
  background: ${palette.white};
  min-height: 100vh;
`;

const MainContent = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 72px 40px;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const HeaderSection = styled.div`
  text-align: center;
  padding-bottom: 24px;
  border-bottom: 1px solid ${palette.outlineGray};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 40px;
  background: ${palette.white};
  border: 1px solid ${palette.outlineGray};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const RequiredMark = styled.span`
  color: ${palette.error};
  font-size: 16px;
  font-weight: 500;
`;

const HelperText = styled.div`
  margin-top: 4px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${palette.outlineGray};
`;

const InfoSection = styled.div`
  display: flex;
  justify-content: center;
`;

const InfoBox = styled.div`
  width: 100%;
  max-width: 600px;
  padding: 32px;
  background: ${palette.primaryLightest};
  border-radius: 12px;
  border: 1px solid ${palette.primary};
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
`;

const IPInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  background: ${palette.primaryLightest};
  border: 1px solid ${palette.primary};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const IPInfoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IPInfoValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-top: 4px;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 16px 48px 16px 16px;
  border: 1px solid ${palette.outlineGray};
  border-radius: 10px;
  font-size: 1rem;
  line-height: 1.5;
  background: ${palette.white};
  color: ${palette.gray800};
  resize: none;
  outline: none;
  font-family: "Pretendard", "Poppins";
  transition: all 0.5s;

  &::placeholder {
    font-weight: 300;
    color: ${palette.gray300};
  }

  &:focus,
  &:hover {
    border-color: ${palette.main};
  }

  &:disabled {
    background: ${palette.gray100};
    color: ${palette.gray400};
    cursor: not-allowed;
  }

  /* 자동완성 스타일 제거 */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px ${palette.white} inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* 드래그 방지 */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${palette.gray500};
  transition: color 0.2s ease;

  &:hover {
    color: ${palette.primary};
  }

  &:focus {
    outline: 2px solid ${palette.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
